import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import PropertyInfoPanel from '../components/scenarios/PropertyInfoPanel';
import ScenarioParametersPanel from '../components/scenarios/ScenarioParametersPanel';
import ResultsPanel from '../components/scenarios/ResultsPanel';
import SaveScenarioModal from '../components/scenarios/SaveScenarioModal';
import ChatBar from '../components/scenarios/ChatBar';
import { calculateScenario, getDefaultAssumptions } from '../utils/scenarioCalculations';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { API_URL } from '../config/api';
import { ArrowLeft, RotateCcw, Save } from 'lucide-react';
import './ScenarioAnalysisPage.css';

const ScenarioAnalysisPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { session } = useAuth();
    const { showToast } = useToast();
    const queryClient = useQueryClient();

    const [searchParams, setSearchParams] = useSearchParams();
    const scenarioIdFromUrl = searchParams.get('scenario_id');
    const loadedFromUrlRef = useRef(false);

    const [assumptions, setAssumptions] = useState(null);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const defaultAssumptions = useRef(null);

    // Fetch property data
    const { data: property, isLoading, error } = useQuery({
        queryKey: ['property', id],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/properties/${id}`);
            if (!response.ok) throw new Error('Property not found');
            return response.json();
        },
        retry: 1,
        onSuccess: (data) => {
            // Initialize assumptions from property data on first load
            if (!assumptions) {
                setAssumptions(getDefaultAssumptions(data));
                if (!defaultAssumptions.current) {
                    defaultAssumptions.current = getDefaultAssumptions(data);
                }
            }
        }
    });

    // Initialize assumptions when property loads (backup for strict mode)
    if (property && !assumptions) {
        setAssumptions(getDefaultAssumptions(property));
        if (!defaultAssumptions.current) {
            defaultAssumptions.current = getDefaultAssumptions(property);
        }
    }

    // Fetch saved scenarios
    const { data: savedScenarios } = useQuery({
        queryKey: ['scenarios', id],
        queryFn: async () => {
            const response = await fetch(`${API_URL}/scenarios?property_id=${id}`, {
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (!response.ok) return [];
            return response.json();
        },
        enabled: !!session,
    });

    // Auto-load scenario from URL param
    useEffect(() => {
        if (scenarioIdFromUrl && savedScenarios && !loadedFromUrlRef.current) {
            const match = savedScenarios.find(s => s.id === scenarioIdFromUrl);
            if (match) {
                loadedFromUrlRef.current = true;
                setAssumptions(match.assumptions);
                showToast(`Loaded "${match.name}"`, 'success');
            }
        }
    }, [scenarioIdFromUrl, savedScenarios, showToast]);

    // Calculate results from current assumptions
    const results = useMemo(() => {
        if (!assumptions) return null;
        return calculateScenario(assumptions);
    }, [assumptions]);

    const handleLoadScenario = useCallback((scenario) => {
        setAssumptions(scenario.assumptions);
        setSearchParams({ scenario_id: scenario.id }, { replace: true });
        loadedFromUrlRef.current = true;
        showToast(`Loaded "${scenario.name}"`, 'success');
    }, [showToast, setSearchParams]);

    const handleDeleteScenario = useCallback(async (scenarioId) => {
        try {
            const response = await fetch(`${API_URL}/scenarios/${scenarioId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (response.ok || response.status === 204) {
                queryClient.invalidateQueries({ queryKey: ['scenarios', id] });
                showToast('Scenario deleted', 'success');
            }
        } catch (err) {
            console.error(err);
            showToast('Error deleting scenario', 'error');
        }
    }, [session, id, queryClient, showToast]);

    const handleSaved = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['scenarios', id] });
    }, [queryClient, id]);

    const handleReset = useCallback(() => {
        if (property) setAssumptions(getDefaultAssumptions(property));
    }, [property]);

    if (isLoading) {
        return (
            <Layout scrollable={true}>
                <div className="loading-container">
                    <p>Loading property details...</p>
                </div>
            </Layout>
        );
    }

    if (error || !property) {
        return (
            <Layout scrollable={true}>
                <div className="error-container">
                    <h2>Property Not Found</h2>
                    <p>{error?.message || 'Unable to load property data.'}</p>
                    <button className="btn-primary" onClick={() => navigate(-1)}>Back</button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout scrollable={true}>
            <div className="scenario-page">
                <div className="scenario-grid">
                    <aside className="scenario-col-left">
                        <PropertyInfoPanel
                            property={property}
                            savedScenarios={savedScenarios || []}
                            onLoadScenario={handleLoadScenario}
                            onDeleteScenario={handleDeleteScenario}
                        />
                    </aside>

                    <main className="scenario-col-center">
                        <div className="scenario-center-header">
                            <button className="scenario-back-link" onClick={() => navigate(-1)}>
                                <ArrowLeft size={16} />
                                Back
                            </button>
                            <h1 className="scenario-title">Scenario Analysis</h1>
                            <div className="scenario-top-actions">
                                <button className="scenario-btn-reset" onClick={handleReset}>
                                    <RotateCcw size={14} />
                                    Reset
                                </button>
                                <button className="scenario-btn-save" onClick={() => setShowSaveModal(true)}>
                                    <Save size={14} />
                                    Save Scenario
                                </button>
                            </div>
                        </div>
                        <div className="scenario-center-body">
                            {assumptions && (
                                <ScenarioParametersPanel
                                    assumptions={assumptions}
                                    setAssumptions={setAssumptions}
                                    property={property}
                                    defaultAssumptions={defaultAssumptions.current}
                                />
                            )}
                        </div>
                    </main>

                    <aside className="scenario-col-right">
                        <ResultsPanel results={results} />
                    </aside>
                </div>
            </div>

            <ChatBar />

            <SaveScenarioModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                propertyId={parseInt(id)}
                assumptions={assumptions}
                results={results}
                onSaved={handleSaved}
            />
        </Layout>
    );
};

export default ScenarioAnalysisPage;
